import React, {useCallback} from 'react';
import styled from 'styled-components';
import {useFormikContext} from 'formik';
import {DrawerFields} from 'components/customer/Product/entity/Drawer';
import {DrawerParameters} from 'components/customer/Product/entity/DrawerParameters';
import {DrawerPosition} from 'components/customer/Product/Drawer/DrawerPosition';
import {SelectedHardware} from 'components/customer/Product/Drawer/SelectedHardware';
import {useInnerDrawerGeometry} from 'components/customer/Product/Drawer/helpers/useInnerDrawerGeometry';
import {spacingAround} from 'components/customer/Product/Drawer/helpers/innerDrawerGeometry';
import {
    DragLimits,
    DragPreview,
} from 'components/customer/Product/Drawer/helpers/useInnerDrawerDrag';
import {Icon} from 'shared/helpers';
import {
    Badge,
    Heading,
    Panel,
    PanelBlock,
    Spacer,
} from 'components/customer/Product/Drawer/layoutStyles';

interface InnerSelectedDrawerProps {
    drawerParams: DrawerParameters;
    drawerAmount: number;
    selectedIndex: number;
    // a drag in flight on the drawing, so the gaps here follow it
    preview?: DragPreview;
    limitsOf: (index: number) => DragLimits;
    // one per drawer, staying with it as drawers go in or come out above it
    keys?: number[];
    canDelete?: boolean;
    onDelete?: (index: number) => void;
}

const format = (value: number) => `${Math.round(value * 10) / 10}mm`;

const NO_KEYS: number[] = [];

// a drawer set over its neighbour has no gap, it overlaps by that much
const gapCell = (value: number, dataCy: string) =>
    value < 0 ? (
        <Overlap data-cy={dataCy}>overlaps ≈{format(-value)}</Overlap>
    ) : (
        <dd data-cy={dataCy}>≈{format(value)}</dd>
    );

// Every drawer stays mounted, so its type defaulting and runner lookup keep
// running; only the selected one is shown.
export const InnerSelectedDrawer = ({
    drawerParams,
    drawerAmount,
    selectedIndex,
    preview = null,
    limitsOf,
    keys = NO_KEYS,
    canDelete = false,
    onDelete,
}: InnerSelectedDrawerProps) => {
    const {values} = useFormikContext<DrawerFields>();
    const geometry = useInnerDrawerGeometry(preview);

    const deleteHandler = useCallback(
        (event: React.MouseEvent<HTMLButtonElement>) => {
            onDelete?.(Number(event.currentTarget.dataset.index));
        },
        [onDelete]
    );

    return (
        <Panel data-cy="inner-selected-drawer">
            {/* a removed drawer leaves the form a render before the count does */}
            {Array.from(
                Array(
                    Math.min(drawerAmount, values.drawers?.length ?? 0)
                ).keys()
            ).map((index) => {
                const spacing = geometry
                    ? spacingAround(geometry, index)
                    : null;

                return (
                    <PanelBlock
                        key={keys[Number(index)] ?? `at-${index}`}
                        hidden={index !== selectedIndex}
                        data-cy={`inner-selected-drawer-${index}`}>
                        <Heading data-cy={`inner-drawer-heading-${index}`}>
                            <Badge>{index + 1}</Badge>
                            <span>Drawer {index + 1}</span>
                            <Spacer />
                            {canDelete ? (
                                <DeleteButton
                                    type="button"
                                    title="Remove this drawer"
                                    aria-label={`Remove drawer ${index + 1}`}
                                    data-index={index}
                                    data-cy={`inner-drawer-delete-${index}`}
                                    onClick={deleteHandler}>
                                    <Icon iconName="Options-Delete.svg" />
                                </DeleteButton>
                            ) : null}
                        </Heading>

                        <DrawerPosition
                            index={index}
                            sharesDraft
                            limitsOf={limitsOf}
                        />
                        <Hint>
                            From the top of the bottom panel to the underside of
                            the drawer box.
                        </Hint>

                        {spacing === null ? null : (
                            <Measures data-cy={`inner-drawer-spacing-${index}`}>
                                <dt>Gap above</dt>
                                {gapCell(
                                    spacing.above,
                                    `inner-drawer-gap-above-${index}`
                                )}
                                <dt>Gap below</dt>
                                {gapCell(
                                    spacing.below,
                                    `inner-drawer-gap-below-${index}`
                                )}
                                <dt>Box height</dt>
                                <dd>{format(spacing.height)}</dd>
                            </Measures>
                        )}
                        <Hint>
                            ≈ Estimated gaps. Final clearances depend on
                            hardware at manufacture.
                        </Hint>

                        <SelectedHardware
                            drawerParams={drawerParams}
                            index={index}
                        />
                    </PanelBlock>
                );
            })}
        </Panel>
    );
};

const DeleteButton = styled.button`
    padding: 0;
    border: 0;
    background: none;
    line-height: 0;

    img {
        width: 22px;
        cursor: pointer;
    }
`;

const Hint = styled.div`
    margin-top: 4px;
    color: #6b7480;
    font-size: 12px;
    text-wrap: pretty;
`;

const Measures = styled.dl`
    display: grid;
    grid-template-columns: auto 1fr;
    gap: 2px 10px;
    margin: 10px 0 0;
    font-size: 12px;

    dt {
        font-weight: 400;
        color: #6b7480;
    }

    dd {
        margin: 0;
        font-weight: 700;
        color: ${({theme}) => theme.colors.primary?.main};
        font-variant-numeric: tabular-nums;
    }
`;

const Overlap = styled.dd`
    color: ${({theme}) => theme.colors.errorLabel?.main} !important;
`;
